import { useCallback } from 'react';

/**
 * THE phone "open this item at the top of the screen" move (owner 2026-10-03,
 * extracted 2026-10-05 from `components/OrderForm.js`'s `openItemOnPhone` so the
 * Breakdown's gag-panel task list does the same). It knows nothing about orders
 * or tasks: the CALLER has already opened its item (so the element below is the
 * opened item's box) and hands over that element plus how to close the others.
 *
 * In this ORDER:
 *   1. the caller's item is open while any other open item STAYS open;
 *   2. the page scrolls the element to the TOP of the screen, just under the
 *      pinned chrome (app bar, tabs, table header — measured at run time, it
 *      differs per page);
 *   3. only when that scroll ends do the others close (`closeOthers`), and the
 *      page is moved by exactly what they took away so the element STAYS at the
 *      top (iOS Safari has no scroll anchoring to do it for us).
 * Closing everything first (the old way) shrank the content above mid-scroll,
 * so the page set off the wrong way and landed short.
 *
 * No element (or no window) → `closeOthers()` at once, no scroll.
 * Pinned by `hooks/useOpenAtTop.test.js`.
 */
export function pinnedChromeBottom() {
  let chrome = 0;
  document.querySelectorAll('.sticky, .fixed, [style*="sticky"], [style*="fixed"]').forEach((el) => {
    const cs = getComputedStyle(el);
    if (cs.position !== 'sticky' && cs.position !== 'fixed') return;
    const r = el.getBoundingClientRect();
    if (!(r.height > 0 && r.height < window.innerHeight / 3 && r.width > window.innerWidth / 2)) return;
    // A sticky bar may not be STUCK yet (at the top of the page it sits lower):
    // count where it PINS (its `top`), never where it happens to be now.
    const pinTop = cs.position === 'sticky' ? parseFloat(cs.top) : r.top;
    if (!Number.isFinite(pinTop) || pinTop > window.innerHeight / 3) return;
    chrome = Math.max(chrome, pinTop + r.height);
  });
  return chrome;
}

/**
 * THE "jump to this row" scroll for a deep link or a cross-view open (the
 * hires Table from a Kanban/Gantt/Calendar click, `?hire=`, `?order=` on the
 * order pages): the row's top lands just UNDER the pinned chrome, never behind
 * it. A bare `scrollIntoView({ block: 'start' })` puts it at y = 0, under the
 * app bar, tabs and pinned table header (owner 2026-10-08: "it scrolls to far
 * and the header for that order is off the top of the screen").
 */
export function scrollUnderPinnedChrome(el) {
  if (!el || typeof el.scrollIntoView !== 'function') return;
  el.style.scrollMarginTop = (pinnedChromeBottom() + 8) + 'px';
  el.scrollIntoView({ block: 'start', behavior: 'smooth' });
}

export function openAtTop(el, closeOthers) {
  if (!el || typeof window === 'undefined') { closeOthers(); return; }
  requestAnimationFrame(() => requestAnimationFrame(() => {
    el.style.scrollMarginTop = (pinnedChromeBottom() + 8) + 'px';
    let done = false;
    const finish = () => {
      if (done) return; done = true;
      window.removeEventListener('scrollend', finish);
      // Ours is the ONLY compensation: Chrome's own scroll anchoring would also
      // react to the collapse and the two fight, so it is off for this one moment.
      const root = document.scrollingElement || document.documentElement;
      const prevAnchor = root.style.overflowAnchor;
      root.style.overflowAnchor = 'none';
      const before = el.getBoundingClientRect().top;
      closeOthers();
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const shift = el.getBoundingClientRect().top - before;
        if (Math.abs(shift) > 1) window.scrollBy({ top: shift, behavior: 'auto' });
        root.style.overflowAnchor = prevAnchor;
      }));
    };
    window.addEventListener('scrollend', finish);
    setTimeout(finish, 700); // browsers without `scrollend`, or no scroll needed
    el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }));
}

/** `const openAtTop = useOpenAtTop();` → `openAtTop(element, closeOthers)`. */
export default function useOpenAtTop() {
  return useCallback((el, closeOthers) => openAtTop(el, closeOthers), []);
}
